import React, { useEffect, useState } from 'react';
import { Col, Row, Form, InputNumber, FormInstance } from 'antd';
import { ShiftDurationInput } from './shift-duration-input';
import { StartDatetimeSelect } from './start-datetime-select';
import { UnitRoleSelect } from './unit-role-select';
import { ShiftTagSelect } from './shift-tag-select';
import { WorkloadWarningBanner } from './_workload-warning-banner';
import { useLazyQuery, useQuery } from '@common/hooks';
import { INDEX_SHIFT_CONFIGS_QUERY } from '@scheduling/graphql';
import { RolesQuery } from '@clinician-profile/graphql';
import { ErrorHandler } from '@common/components';
import { useFeatureFlags } from '@common/contexts/feature-flags';
import moment from 'moment-timezone';
import { OrganizationUnitShiftConfigFlatType } from '@scheduling/graphql/types';

export type CreateShiftFormValues = {
  shiftDuration: number[];
  organizationUnitId: string;
  roleId: string;
  startDate: string;
  startTime: string;
  openings: number;
  shiftGapTagIds: string[];
};

type ShiftRequestFormProps = {
  form: FormInstance;
  handleSubmit: (values: CreateShiftFormValues) => void;
  startDate?: string;
};

export function CreateShiftForm({
  form,
  handleSubmit,
  startDate,
}: ShiftRequestFormProps) {
  const [shiftConfigs, setShiftConfigs] = useState<
    OrganizationUnitShiftConfigFlatType[]
  >([]);

  const [selectedStartTime, setSelectedStartTime] = useState<
    string | undefined
  >(undefined);
  const [organizationUnitId, setOrganizationUnitId] = useState<string | null>(
    null
  );
  const [roleId, setRoleId] = useState<string | null>(null);

  const { isWorkloadExpressionEnabled } = useFeatureFlags();
  const watchedStartDate = Form.useWatch('startDate', form);

  const {
    loading: rolesLoading,
    error: rolesError,
    data: rolesData,
  } = useQuery(RolesQuery, {
    variables: { organizationUnitId: organizationUnitId ?? undefined },
    fetchPolicy: 'cache-and-network',
  });

  const [
    shiftConfigQuery,
    { loading: loadingShiftConfigs, error: errorShiftConfigs },
  ] = useLazyQuery(INDEX_SHIFT_CONFIGS_QUERY, {
    fetchPolicy: 'cache-and-network',
    onCompleted: (data) => {
      const configs: OrganizationUnitShiftConfigFlatType[] =
        data.organizationUnitShiftConfigs?.map((config) => ({
          ...config,
          startTime: moment.parseZone(config.startTime),
          endTime: moment.parseZone(config.endTime),
        })) || [];

      setShiftConfigs(configs);
    },
  });

  useEffect(() => {
    if (organizationUnitId) {
      shiftConfigQuery({
        variables: {
          filters: { role: roleId, unit: organizationUnitId },
        },
      });
    }
  }, [organizationUnitId, roleId, shiftConfigQuery]);

  useEffect(() => {
    if (startDate) {
      form.setFieldsValue({ startDate: moment(startDate) });
    }
  }, [form, startDate]);

  const onFormValuesChange = (changed: {
    organizationUnitId: string;
    roleId: string;
  }) => {
    const newOrganizationUnitId = changed.organizationUnitId;
    const newRoleId = changed.roleId;

    if (newOrganizationUnitId || newRoleId) {
      setShiftConfigs([]);
      setSelectedStartTime(undefined);
      form.setFieldsValue({
        startTime: undefined,
        shiftDuration: undefined,
      });
    }

    if (newOrganizationUnitId) {
      setOrganizationUnitId(newOrganizationUnitId);
      setRoleId(null);
      form.setFieldsValue({ roleId: undefined });
    }
    if (newRoleId) {
      setRoleId(newRoleId);
    }
  };

  return (
    <>
      <Form
        className="CreateShiftForm"
        form={form}
        onValuesChange={onFormValuesChange}
        onFinish={handleSubmit}
        layout="vertical"
      >
        <Row gutter={[8, 8]}>
          <UnitRoleSelect
            rolesData={rolesData?.roles}
            rolesLoading={rolesLoading}
            rolesError={rolesError}
            permittedDepartment
          />
          <StartDatetimeSelect
            form={form}
            shiftConfigs={shiftConfigs}
            onTimeSelect={setSelectedStartTime}
            startTime={selectedStartTime}
            loading={loadingShiftConfigs}
          />
          <ShiftDurationInput
            shiftConfigs={shiftConfigs}
            startTime={selectedStartTime}
            form={form}
            loading={loadingShiftConfigs}
          />
          <Col span={12}>
            <Form.Item
              label="Openings"
              name="openings"
              initialValue={1}
              rules={[
                {
                  required: true,
                  message: 'Please enter the number of openings',
                },
                {
                  type: 'number',
                  min: 1,
                  max: 99,
                  message: 'Please enter a number between 1 and 99',
                },
              ]}
            >
              <InputNumber
                size="large"
                style={{
                  width: '100%',
                }}
                defaultValue={1}
                min={1}
                max={99}
              />
            </Form.Item>
          </Col>
          {errorShiftConfigs && (
            <Col span={24}>
              <ErrorHandler error={errorShiftConfigs} log="ShiftForm" />
            </Col>
          )}
          {isWorkloadExpressionEnabled && (
            <Col span={24}>
              <WorkloadWarningBanner
                organizationUnitId={organizationUnitId}
                roleId={roleId}
                startDate={watchedStartDate}
              />
            </Col>
          )}
          <ShiftTagSelect
            organizationUnitId={organizationUnitId ?? undefined}
          />
        </Row>
      </Form>
      <style jsx>
        {`
          :global(.CreateShiftForm .ant-input-number),
          :global(.CreateShiftForm .ant-form-item-control-input-content)
            :global(.CreateShiftForm .ant-picker) {
            width: 100%;
          }
        `}
      </style>
    </>
  );
}
